import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';
import Preloader from 'components/Preloader';
import { updateMenuSettings, getMenusSettings } from 'redux/actions/Menu/menu';
import { PaymentSettings } from 'components/Settings';
import LabelTooltip from 'components/FormFields/LabelTooltip';
import {
  Grid,
  Button,
  Box,
  Switch,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FormGroup from '@mui/material/FormGroup';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import CustomTextInput from 'components/Forms/CustomTextInput';
import ConfirmPromptModal from 'components/ConfirmPromptModal/ConfirmPromptModal';
import { useTranslation } from 'react-i18next';
import ConfirmProviderAction from 'components/Confirm/ConfirmProviderAction';
import { useAutoConfirmModal } from 'hooks/useAutoConfirmModal';
import PhoneImage from 'assets/images/PhoneIcon.png';
import { mainSettingsInfo } from './schema';
import MinOrderSettings from 'pages/WebsiteSettings/MinOrder/MinOrderSettings';
import { priceTransform } from 'helpers/formPrice';
import usePermission from 'hooks/permission';
import * as all from 'constants/permissions';
import BaseUrlInput from 'pages/WebsiteSettings/BaseUrl/BaseUrlInput';
import { DropOfDivider } from 'pages/WebsiteSettings/DropOfOptions/styles';
import DropOfOptions from 'pages/WebsiteSettings/DropOfOptions/DropOfOptions';

const minOrderDefaultData = {
  delivery: { amount: 0, enabled: false },
  pickup: { amount: 0, enabled: false },
};

const Website = () => {
  const { t } = useTranslation();
  const { openAutoConfirmModal, handleCloseAutoConfirmModal } =
    useAutoConfirmModal();
  const [openPaymentModal, setOpenPaymentModal] = useState(false);

  const dispatch = useDispatch();
  const formRef = useRef();
  const [model, setModel] = useState({
    minOrder: minOrderDefaultData,
    merchantSettings: {
      applePayEnabled: false,
      googlePayEnabled: false,
    },
  });
  const checkPermission = usePermission();
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [creditCard, setCreditCard] = useState(null);
  const [isCreditChange, setIsCreditChange] = useState(false);
  const [isPickupChanged, setIsPickupChanged] = useState(false);
  const [isDeliveryChanged, setIsDeliveryChanged] = useState(false);
  const [delivery, setDelivery] = useState(null);
  const [surCharge, setSurCharge] = useState(null);

  const websiteFormMethods = useForm({
    defaultValues: model,
    mode: 'all',
    resolver: yupResolver(mainSettingsInfo),
  });
  const { reset, register, setValue, getValues } = websiteFormMethods;

  const baseUrl = useMemo(() => {
    const baseUrl = model?.baseUrl;
    return `${baseUrl || process.env.REACT_APP_FOOD2_URL}/${model?.url}`;
  }, [model?.baseUrl, model?.url]);

  useEffect(() => {
    return () => {
      dispatch({ type: 'PROVIDERS_START_RESUME_RESET' });
    };
  }, []);

  const handleGetMenuSettings = () => {
    setLoading(true);
    getMenusSettings().then((data) => {
      if (data.settings) {
        setCreditCard(
          !data.settings.payment ? false : data.settings.creditCard
        );
        setDelivery(
          !data.settings.creditCard || !data.settings.payment
            ? false
            : data.settings.delivery
        );

        if (!data.settings.payment) {
          data.settings.creditCard = false;
          data.settings.delivery = false;
        }
        const merchantSettings = {
          applePayEnabled:
            data.settings?.merchantSettings?.applePayEnabled ?? false,
          googlePayEnabled:
            data.settings?.merchantSettings?.googlePayEnabled ?? false,
        };
        if (
          Object.prototype.hasOwnProperty.call(
            data.settings?.merchantSettings ?? {},
            'surchargePercent'
          )
        ) {
          setSurCharge(data.settings?.merchantSettings?.surchargePercent);
        } else {
          setSurCharge(null);
        }

        setModel({
          ...data.settings,
          merchantSettings: merchantSettings,
        });

        if (
          !data?.settings?.minOrderObj ||
          (!Object.prototype.hasOwnProperty.call(
            data?.settings?.minOrderObj ?? {},
            'delivery'
          ) &&
            !Object.prototype.hasOwnProperty.call(
              data?.settings?.minOrderObj ?? {},
              'pickup'
            ))
        ) {
          reset(
            {
              ...data.settings,
              minOrderObj: minOrderDefaultData,
              merchantSettings: merchantSettings,
            },
            { keepDirty: false }
          );
        } else {
          reset(
            {
              ...data.settings,
              merchantSettings: merchantSettings,
            },
            { keepDirty: false }
          );
        }
      }
      setTimeout(() => {
        setLoading(false);
      }, 500);
    });
  };

  useEffect(() => {
    handleGetMenuSettings();
  }, []);

  const handleSubmit = (values) => {
    if (openPaymentModal) {
      return;
    }
    setDisabled(true);
    setLoading(true);
    setIsCreditChange(false);
    const websiteSettingsModel = {
      ...model,
      ...values,
      theme: model.theme,
    };
    setIsPickupChanged(false);
    setIsDeliveryChanged(false);
    websiteSettingsModel.creditCard = creditCard;
    delete websiteSettingsModel.payment;
    if (surCharge !== null) {
      websiteSettingsModel.merchantSettings.surchargePercent = surCharge;
    } else {
      delete websiteSettingsModel.merchantSettings.surchargePercent;
    }
    dispatch(updateMenuSettings(websiteSettingsModel, 'website'))
      .then((res) => {
        websiteSettingsModel.payment = res?.value?.settings?.payment;
        setModel(websiteSettingsModel);
        setDisabled(false);
        dispatch({ type: 'PROVIDERS_START_RESUME_RESET' });
        handleGetMenuSettings();
      })
      .finally(() => {
        setDisabled(false);
        setLoading(false);
      });
  };

  const onDisconnect = useCallback(() => {
    const newModel = { ...model };
    newModel.delivery = false;
    newModel.creditCard = false;
    newModel.payment = false;
    setCreditCard(!newModel.payment ? false : newModel.creditCard);
    setModel(newModel);
    handleGetMenuSettings();
  }, [model]);

  const onConnect = useCallback(() => {
    formRef.current.submit();
  }, []);

  const handleCreditCardChange = (e) => {
    const checked = e.target.checked;
    setCreditCard(checked);
    setIsCreditChange(true);
    if (!checked) {
      setDelivery(false);
      setValue('delivery', false, { shouldDirty: true });
    }
  };

  const onChangeDelivery = (e) => {
    setDelivery(e.target.checked);
    setValue('delivery', e.target.checked, { shouldDirty: true });
    setIsDeliveryChanged(true);
  };

  const handleOptionsChange = (event) => {
    const { checked } = event.target;
    setIsDeliveryChanged(true);
    setValue('deliveryBy.0.dropoffVerification.leaveAtDoor.enabled', checked, {
      shouldDirty: true,
    });
  };

  const handleGooglePayApplePayChange = (event) => {
    const { name, checked } = event.target;
    const [parentKey, childKey] = name?.split('.');
    setModel((prev) => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [childKey]: checked,
      },
    }));
    setValue(name, checked, { shouldDirty: true });
  };

  const handlePickupChange = (event) => {
    setValue('minOrderObj.pickup.enabled', event.target.checked, {
      shouldDirty: true,
    });
    setIsPickupChanged(true);
  };

  const handleDeliveryChangeChange = (event) => {
    setValue('minOrderObj.delivery.enabled', event.target.checked, {
      shouldDirty: true,
    });
    setIsDeliveryChanged(true);
  };
  const onChangeAmount = (event) => {
    const { name, value } = event.target;
    const findValue = priceTransform(value);
    setValue(name, findValue, { shouldDirty: true });
  };
  return (
    <Box>
      <Box>
        <ConfirmPromptModal
          handleConfirm={websiteFormMethods.handleSubmit(handleSubmit)}
          submitData={websiteFormMethods.getValues()}
          hasUnsavedChanges={
            websiteFormMethods.formState.isDirty ||
            isCreditChange ||
            isPickupChanged ||
            isDeliveryChanged
          }
          formState={websiteFormMethods}
        />
        <ConfirmProviderAction
          handleClose={handleCloseAutoConfirmModal}
          showModal={openAutoConfirmModal}
          title={'website_settings.auto_confirm'}
          description={'website_settings.auto_confirm_desc'}
          label={''}
          icon={PhoneImage}
          iconName={'support phone number'}
          phoneNumber={'website_settings.phoneNumber'}
        />
        <FormProvider {...websiteFormMethods}>
          {loading ? (
            <Preloader />
          ) : (
            <form
              onSubmit={websiteFormMethods.handleSubmit(handleSubmit, (err) =>
                console.log(err)
              )}
            >
              <Grid container columns={{ xs: 9 }} spacing={2}>
                <Grid item xs={12}>
                  <h5 className="mb-2">
                    {t('website_settings.pick-up_settings')}
                  </h5>
                  <div className="d-flex ">
                    <FormGroup sx={{ flexDirection: 'row', display: 'flex' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            {...register('pickup')}
                            defaultChecked={model.pickup}
                            name="pickup"
                          />
                        }
                        label={t('website_settings.pickup')}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            {...register('curbsidePickup')}
                            defaultChecked={model.curbsidePickup}
                            name="curbsidePickup"
                          />
                        }
                        label={t('website_settings.curbside_pickup')}
                      />
                    </FormGroup>
                  </div>
                </Grid>

                <Grid item xs={12}>
                  <h5 className="mb-2">
                    {t('website_settings.delivery_settings')}
                  </h5>
                  <Grid item xs={12}>
                    <FormControl onChange={onChangeDelivery}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!delivery}
                            name="delivery"
                            disabled={!creditCard}
                          />
                        }
                        label={
                          <LabelTooltip
                            value={t('website_settings.delivery')}
                            id="deliveryTooltip"
                            tooltip="Not available without Merchant Integration!"
                          />
                        }
                      />
                    </FormControl>
                  </Grid>
                  {delivery && (
                    <>
                      <DropOfDivider />
                      <DropOfOptions
                        model={websiteFormMethods.getValues()}
                        handleOptionsChange={handleOptionsChange}
                      />
                      <DropOfDivider />
                    </>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <h5 className="mb-4">
                    {' '}
                    {t('website_settings.marketplace_settings')}
                  </h5>
                  <MinOrderSettings
                    modelValues={getValues()}
                    handlePickupChange={handlePickupChange}
                    handleDeliveryChangeChange={handleDeliveryChangeChange}
                    onChangeAmount={onChangeAmount}
                  />
                  <Grid container spacing={2} columns={{ xs: 9 }}>
                    <Grid item xs={2}>
                      <CustomTextInput
                        disabled={true}
                        defaultValue={baseUrl}
                        helperText={
                          <Link target="blanc" href={baseUrl}>
                            {baseUrl}
                          </Link>
                        }
                        sx={{ width: '100%' }}
                        inputProps={{
                          type: 'string',
                        }}
                        label={
                          <LabelTooltip
                            required
                            value={t('website_settings.marketplace_alias')}
                          />
                        }
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip
                                title={t(
                                  'website_settings.your_personalized_website'
                                )}
                              >
                                <HelpOutlineIcon fontSize={'small'} />
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                        name="url"
                      />
                    </Grid>
                  </Grid>
                </Grid>
                {checkPermission(all['SUPER']) ? (
                  <Grid item xs={2}>
                    <BaseUrlInput />
                  </Grid>
                ) : null}
                <Grid item xs={12}>
                  <PaymentSettings
                    register={register}
                    setCreditCard={handleCreditCardChange}
                    model={model}
                    payment={model?.payment}
                    onConnect={onConnect}
                    creditCard={creditCard}
                    onDisconnect={onDisconnect}
                    openPaymentModal={openPaymentModal}
                    setOpenPaymentModal={setOpenPaymentModal}
                    websiteFormMethods={websiteFormMethods}
                    handleGooglePayApplePayChange={
                      handleGooglePayApplePayChange
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    disabled={disabled}
                    variant="contained"
                    color="primary"
                    size="lg"
                    className="px-5"
                    type="submit"
                  >
                    {t('website_settings.update')}
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </FormProvider>
      </Box>
    </Box>
  );
};

export default Website;
